import { useMsal } from '@azure/msal-react'
import {
  faBan,
  faBullhorn,
  faBullseye,
  faChartLine,
  faCheckCircle,
  faClock,
  faDollarSign,
  faLocationDot,
  faPlay,
  faQuestionCircle,
  faStore,
  faTag,
  faTv,
  faUpload,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Accordion,
  Box,
  Button,
  Card,
  Center,
  Container,
  Divider,
  Flex,
  Group,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from '@mantine/core'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { loginRequest } from '../authConfig'
import { AllLocationsCtaBanner } from '../components/location/AllLocationsCtaBanner'
import { COLORS } from '../utils/constants'

// --- Advertiser content (primary) ---
const advertiserSteps = [
  {
    icon: faLocationDot,
    title: 'Pick Your Locations',
    description:
      'Browse local venues and choose where your ad appears — or select every location at once.',
  },
  {
    icon: faUpload,
    title: 'Upload Your Ad',
    description:
      'Add your own design, or let our team create one for you. Set your schedule and budget.',
  },
  {
    icon: faPlay,
    title: 'Go Live',
    description:
      'Once approved, your ad starts running on smart TVs in front of local customers.',
  },
]

const advertiserBenefits = [
  {
    icon: faBullseye,
    title: 'Hyper-Local Targeting',
    description:
      'Reach customers right where they shop, dine, and gather — not strangers driving past a billboard.',
  },
  {
    icon: faDollarSign,
    title: 'A Fraction of Billboard Cost',
    description:
      'Get your business on screens across town for far less than traditional out-of-home advertising.',
  },
  {
    icon: faClock,
    title: 'Flexible Scheduling',
    description:
      'Run your ad by the week or month, and start or stop whenever you want. No long-term contracts.',
  },
  {
    icon: faChartLine,
    title: 'Track Your Results',
    description:
      'See how many times each ad plays and how many impressions it earns — plus built-in QR code tracking to count real-world scans.',
  },
]

const advertiserFaqs = [
  {
    question: 'How much does it cost to advertise?',
    answer:
      'Pricing is set by each location and typically runs a fraction of what a billboard costs. You can advertise at a single venue or book every location at once — and you always see the exact price before you pay.',
  },
  {
    question: 'Where will my ad show?',
    answer:
      'Your ad appears on smart TV screens inside local businesses — the places your customers already visit. You choose the specific locations, or reach them all.',
  },
  {
    question: 'Do I need to design my own ad?',
    answer:
      'Not at all. Upload your own artwork, or pay a small design fee and our team will create a professional ad for you.',
  },
  {
    question: 'How long until my ad goes live?',
    answer:
      'After checkout, ads are reviewed for approval — either by the host or our content team — and typically begin running within 48 hours.',
  },
]

// --- Host content (secondary) ---
const hostSteps = [
  {
    icon: faUserPlus,
    title: 'Sign Up',
    description:
      'Hosts join our network by creating an account on our website.',
  },
  {
    icon: faTv,
    title: 'Download the App',
    description:
      'Install our smart TV app for FREE and start displaying local ads.',
  },
  {
    icon: faDollarSign,
    title: 'Start Earning',
    description:
      'Advertisers choose your location, and you split the profit evenly with us.',
  },
]

const hostFeatures = [
  {
    icon: faTag,
    title: 'Set Your Price',
    description: 'Hosts decide the daily advertising rate for their location.',
  },
  {
    icon: faBan,
    title: 'Restrict Business Categories',
    description:
      'Block ads from business categories you don’t want to display.',
  },
  {
    icon: faCheckCircle,
    title: 'Flexible Approval',
    description:
      'Approve ads yourself or let us review them for appropriateness.',
  },
  {
    icon: faBullhorn,
    title: 'Support Local Business',
    description:
      'Give nearby advertisers an affordable way to reach your customers.',
  },
]

const hostFaqs = [
  {
    question: 'Is it really free to become a Host?',
    answer:
      'Yes! It’s completely free to become a Host. There are no monthly fees, no hidden charges, and no cost to download our Smart TV app. Just plug in, join our network, and start earning while supporting local businesses.',
  },
  {
    question: 'Do I need special hardware?',
    answer:
      'All you need is a compatible smart TV and an internet connection. Download our app and you’re ready to go.',
  },
  {
    question: 'How much can I earn as a host?',
    answer:
      'Hosts typically earn between $200 and $500+ per month, depending on pricing and ad volume.',
  },
  {
    question: 'Can I choose which ads display?',
    answer:
      'Yes! You can block certain categories and approve ads manually or let us review them for appropriateness.',
  },
]

const About: React.FC = () => {
  const theme = useMantineTheme()
  const { instance } = useMsal()
  const navigate = useNavigate()

  const handleGetStarted = () => {
    const account = instance.getActiveAccount()
    if (!account) {
      instance.loginRedirect(loginRequest).catch((error) => console.log(error))
    } else {
      navigate('/')
    }
  }

  const handleBookAllLocations = () => {
    const account = instance.getActiveAccount()
    if (!account) {
      instance.loginRedirect(loginRequest).catch((error) => console.log(error))
    } else {
      navigate('/configure-ad/all-locations')
    }
  }

  return (
    <Box bg={theme.colors.gray[0]}>
      {/* Hero Section — Advertiser first */}
      <Box
        style={{
          background: `linear-gradient(135deg, ${theme.colors.green[1]} 0%, ${theme.colors.teal[0]} 100%)`,
          padding: '64px 0 48px 0',
        }}
      >
        <Container size='lg'>
          <Stack align='center' gap='md'>
            <Title order={1} ta='center' fz={{ base: 32, sm: 44 }} fw={900}>
              Reach Local Customers on Screens Across Your Community
            </Title>
            <Text ta='center' fz={{ base: 'lg', sm: 'xl' }} maw={720}>
              Hidden Gemz puts your business on smart TV screens inside the local
              spots your customers already visit — hyper-local advertising for a
              fraction of the cost of a billboard.
            </Text>
            <Button
              size='lg'
              radius='xl'
              mt='md'
              onClick={handleGetStarted}
              style={{ backgroundColor: COLORS.HiddenGemzRed }}
            >
              Start Advertising
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Why Advertise With Us */}
      <Container size='lg' py={56}>
        <Stack align='center' gap='xl'>
          <Title order={2} fz={32} fw={800} ta='center'>
            Why Advertise With Us
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing='xl' w='100%'>
            {advertiserBenefits.map((benefit) => (
              <Card
                key={benefit.title}
                shadow='md'
                radius='lg'
                p='xl'
                withBorder
              >
                <Center mb='md'>
                  <FontAwesomeIcon
                    icon={benefit.icon}
                    size='2x'
                    color={theme.colors.green[6]}
                  />
                </Center>
                <Title order={4} ta='center' mb='xs'>
                  {benefit.title}
                </Title>
                <Text ta='center' c='dimmed'>
                  {benefit.description}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>

      {/* How to Advertise — Flow Style */}
      <Box py={48} bg={theme.colors.green[0]}>
        <Container size='lg'>
          <Stack align='center' gap='xl'>
            <Title order={2} fz={32} fw={800} ta='center'>
              How to Advertise
            </Title>
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              align='center'
              justify='center'
              gap='lg'
            >
              {advertiserSteps.map((step, index) => (
                <React.Fragment key={step.title}>
                  <Card shadow='md' radius='lg' p='xl' withBorder w={240}>
                    <Center mb='md'>
                      <FontAwesomeIcon
                        icon={step.icon}
                        size='2x'
                        color={theme.colors.teal[6]}
                      />
                    </Center>
                    <Title order={4} ta='center' mb='xs'>
                      {step.title}
                    </Title>
                    <Text ta='center' c='dimmed'>
                      {step.description}
                    </Text>
                  </Card>
                  {index < advertiserSteps.length - 1 && (
                    <>
                      <Text
                        visibleFrom='sm'
                        fz={32}
                        fw={700}
                        c={theme.colors.teal[6]}
                      >
                        →
                      </Text>
                      <Text
                        hiddenFrom='sm'
                        fz={32}
                        fw={700}
                        c={theme.colors.teal[6]}
                      >
                        ↓
                      </Text>
                    </>
                  )}
                </React.Fragment>
              ))}
            </Flex>
          </Stack>
        </Container>
      </Box>

      {/* All-Locations Value */}
      <Container size='lg' py={56}>
        <Stack gap='lg'>
          <Title order={2} fz={32} fw={800} ta='center'>
            One Ad, Every Screen
          </Title>
          <Text ta='center' fz='lg' c='dimmed' maw={700} mx='auto'>
            Want maximum reach? Book every location in the network with a single
            click and run your ad on every screen at once.
          </Text>
          <AllLocationsCtaBanner onBookAllLocations={handleBookAllLocations} />
        </Stack>
      </Container>

      {/* Advertiser FAQ */}
      <Box py={48} bg={theme.colors.green[0]}>
        <Container size='lg'>
          <Stack align='center' gap='xl'>
            <Title order={2} fz={32} fw={800} ta='center'>
              Advertiser FAQ
            </Title>
            <Accordion
              maw={800}
              w='100%'
              variant='separated'
              radius='md'
              multiple
              defaultValue={advertiserFaqs.map((faq) => faq.question)}
            >
              {advertiserFaqs.map((faq, idx) => (
                <Accordion.Item value={faq.question} key={idx}>
                  <Accordion.Control
                    icon={
                      <ThemeIcon color='teal' variant='light'>
                        <FontAwesomeIcon icon={faQuestionCircle} />
                      </ThemeIcon>
                    }
                  >
                    {faq.question}
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text c='dimmed'>{faq.answer}</Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Stack>
        </Container>
      </Box>

      {/* Advertiser CTA */}
      <Container size='sm' py={56}>
        <Stack align='center' gap='md'>
          <Title order={3} ta='center' fz={28} fw={800}>
            Ready to reach your local community?
          </Title>
          <Button
            size='lg'
            radius='xl'
            mt='md'
            onClick={handleGetStarted}
            style={{ backgroundColor: COLORS.HiddenGemzRed }}
          >
            Start Advertising
          </Button>
        </Stack>
      </Container>

      {/* ================= HOST SECTION (secondary) ================= */}
      <Box bg={theme.colors.gray[1]} py={8}>
        <Container size='lg'>
          <Divider
            my='xl'
            label={
              <Text fw={700} fz='sm' c='dimmed' tt='uppercase'>
                Own a screen? Become a Host
              </Text>
            }
            labelPosition='center'
          />
        </Container>
      </Box>

      {/* Who Hosts Are */}
      <Container size='lg' py={40}>
        <Stack align='center' gap='md'>
          <Title order={2} fz={32} fw={800} ta='center'>
            Turn Your Screens Into Income
          </Title>
          <Text ta='center' fz='lg' c='dimmed' maw={720}>
            If you own a business with a smart TV, you can join our network for
            FREE, display local ads, and earn passive income — all while
            supporting nearby businesses.
          </Text>
          <Group>
            <FontAwesomeIcon
              icon={faStore}
              size='2x'
              color={theme.colors.green[6]}
            />
          </Group>
        </Stack>
      </Container>

      {/* How To Become a Host - Flow Style */}
      <Box py={48} bg={theme.colors.green[0]}>
        <Container size='lg'>
          <Stack align='center' gap='xl'>
            <Title order={2} fz={32} fw={800} ta='center'>
              How To Become a Host
            </Title>
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              align='center'
              justify='center'
              gap='lg'
            >
              {hostSteps.map((step, index) => (
                <React.Fragment key={step.title}>
                  <Card shadow='md' radius='lg' p='xl' withBorder w={240}>
                    <Center mb='md'>
                      <FontAwesomeIcon
                        icon={step.icon}
                        size='2x'
                        color={theme.colors.teal[6]}
                      />
                    </Center>
                    <Title order={4} ta='center' mb='xs'>
                      {step.title}
                    </Title>
                    <Text ta='center' c='dimmed'>
                      {step.description}
                    </Text>
                  </Card>
                  {index < hostSteps.length - 1 && (
                    <>
                      <Text
                        visibleFrom='sm'
                        fz={32}
                        fw={700}
                        c={theme.colors.teal[6]}
                      >
                        →
                      </Text>
                      <Text
                        hiddenFrom='sm'
                        fz={32}
                        fw={700}
                        c={theme.colors.teal[6]}
                      >
                        ↓
                      </Text>
                    </>
                  )}
                </React.Fragment>
              ))}
            </Flex>
          </Stack>
        </Container>
      </Box>

      {/* Revenue Split */}
      <Box py={48} bg={theme.colors.green[0]}>
        <Container size='sm'>
          <Stack align='center' gap={0}>
            <Title order={2} ta='center' mb='md'>
              Share the Profits
            </Title>
            <Flex align='center' justify='center' w='100%'>
              <Text
                fw={700}
                fz={20}
                c={COLORS.LightGreen}
                style={{ minWidth: 90, textAlign: 'right' }}
              >
                Host Locations 50%
              </Text>
              <RingProgress
                size={160}
                thickness={18}
                sections={[
                  {
                    value: 50,
                    color: COLORS.HiddenGemzGold,
                    tooltip: 'Us: 50%',
                  },
                  { value: 50, color: COLORS.LightGreen, tooltip: 'Host: 50%' },
                ]}
                mb={16}
                style={{ margin: '0 24px' }}
              />
              <Text
                fw={700}
                fz={20}
                c={COLORS.HiddenGemzGold}
                style={{ minWidth: 90, textAlign: 'left' }}
              >
                Hidden Gemz 50%
              </Text>
            </Flex>
            <Text ta='center' fz='lg' fw={600} mt='sm'>
              Hosts can earn $500+ per month.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Host Features & Benefits */}
      <Container size='lg' py={56}>
        <Stack align='center' gap='xl'>
          <Title order={2} fz={32} fw={800} ta='center'>
            Host Features & Benefits
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing='xl' w='100%'>
            {hostFeatures.map((feature) => (
              <Card
                key={feature.title}
                shadow='md'
                radius='lg'
                p='xl'
                withBorder
              >
                <Center mb='md'>
                  <FontAwesomeIcon
                    icon={feature.icon}
                    size='2x'
                    color={theme.colors.green[6]}
                  />
                </Center>
                <Title order={4} ta='center' mb='xs'>
                  {feature.title}
                </Title>
                <Text ta='center' c='dimmed'>
                  {feature.description}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Stack>
      </Container>

      {/* Host Pricing Example */}
      <Box py={48} bg={theme.colors.green[0]}>
        <Container size='sm'>
          <Text ta='center' fz='xl' fw={500}>
            Most hosts charge $50-$200/month per ad slot — which can mean $500+
            per month in earnings depending on the number of advertisers.
          </Text>
        </Container>
      </Box>

      {/* Host FAQ */}
      <Box py={48}>
        <Container size='lg'>
          <Stack align='center' gap='xl'>
            <Title order={2} fz={32} fw={800} ta='center'>
              Host FAQ
            </Title>
            <Accordion
              maw={800}
              w='100%'
              variant='separated'
              radius='md'
              multiple
              defaultValue={hostFaqs.map((faq) => faq.question)}
            >
              {hostFaqs.map((faq, idx) => (
                <Accordion.Item value={faq.question} key={idx}>
                  <Accordion.Control
                    icon={
                      <ThemeIcon color='teal' variant='light'>
                        <FontAwesomeIcon icon={faQuestionCircle} />
                      </ThemeIcon>
                    }
                  >
                    {faq.question}
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Text c='dimmed'>{faq.answer}</Text>
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Stack>
        </Container>
      </Box>

      {/* Community Support */}
      <Box py={48} bg={theme.colors.green[0]}>
        <Container size='sm'>
          <Text ta='center' fz='xl' fw={500}>
            We believe in keeping money in the community. By connecting local
            advertisers with local hosts, we create a win-win partnership that
            supports small businesses and strengthens local economies.
          </Text>
        </Container>
      </Box>

      {/* Final CTA */}
      <Container size='sm' py={56}>
        <Stack align='center' gap='md'>
          <Title order={3} ta='center' fz={28} fw={800}>
            Advertise or host today — join our growing local network.
          </Title>
          <Group justify='center' mt='md'>
            <Button
              size='lg'
              radius='xl'
              onClick={handleGetStarted}
              style={{ backgroundColor: COLORS.HiddenGemzRed }}
            >
              Start Advertising
            </Button>
            <Button
              size='lg'
              radius='xl'
              variant='outline'
              color='green'
              onClick={handleGetStarted}
            >
              Become a Host
            </Button>
          </Group>
        </Stack>
      </Container>
    </Box>
  )
}

export default About
